import { useEffect } from 'react';

import LogoClinical from '@assets/logo-clinical.png';
import LogoPlus from '@assets/logo-plus.png';
import SejaPlus from '@assets/seja-plus.png';
import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@components/ui/form';
import { pages } from '@constants/pages';
import { useAuth } from '@contexts/auth';
import { useHeader } from '@contexts/header';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import {
  BriefcaseMedical,
  Calculator,
  FileText,
  PanelRightOpen,
  Search as SearchIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';

import { SmartBanner } from './SmartBanner';
import { useSidebar } from './ui/sidebar/useSidebar';

const formSchema = z.object({
  search: z.string(),
});

export function Header() {
  const { backTitle, title } = useHeader();
  const { rightSidebar } = useSidebar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      search: searchParams.get('q') || '',
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    navigate(`${pages.search}?q=${values.search}`);
  }

  useEffect(() => {
    if (pathname !== pages.search) {
      form.reset({ search: '' });
    }
  }, [form, pathname]);

  return (
    <div className="fixed top-0 z-10 flex w-full flex-col">
      <SmartBanner />
      <header
        className={cn(
          'gradient-background shadow-primary/40 w-full shadow-md',
        )}>
        <nav
          className={cn(
            'grid h-[70px] grid-cols-[1fr_auto] items-center px-4 sm:gap-x-4 lg:grid-cols-[auto_1fr_auto]',
          )}>
          <div className="hidden flex-row items-center gap-4 lg:flex">
            <Link className="w-40" to={pages.home}>
              <img
                alt="GPMED logo"
                src={user?.accessGranted ? LogoPlus : LogoClinical}
              />
            </Link>
            {!user?.accessGranted && (
              <Link className="w-28" to={pages.payment}>
                <img alt="Plus" src={SejaPlus} title="Seja plus" />
              </Link>
            )}
          </div>

          <Form {...form}>
            <form
              className="flex justify-center overflow-hidden rounded-lg border-none"
              onSubmit={form.handleSubmit(onSubmit)}>
              <div
                className={cn(
                  'border-background text-background has-[:focus-visible]:border-secondary has-[:focus-visible]:ring-ring grid w-full max-w-md grid-cols-[auto_1fr] items-center gap-2 overflow-hidden rounded-lg border-2 px-4 transition-all duration-300 has-[:focus-visible]:outline-none has-[:focus-visible]:ring-1',
                )}>
                <SearchIcon strokeWidth={1.5} />
                <FormField
                  control={form.control}
                  name="search"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <input
                          className="text-background placeholder:text-background w-full border-0 bg-transparent py-2 text-base font-normal outline-none ring-0"
                          placeholder="Buscar por..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>

          <div
            className={cn(
              'flex h-full flex-row justify-end transition-all md:min-w-96',
              rightSidebar.open && rightSidebar.openMobile
                ? 'invisible opacity-0'
                : 'visible opacity-100',
            )}>
            <NavLink
              className={({ isActive }) =>
                cn(
                  'text-background hidden h-full w-24 flex-col items-center justify-center border-t-2 border-transparent transition-all duration-200 md:flex',
                  isActive
                    ? 'border-secondary text-secondary'
                    : 'hover:text-secondary',
                )
              }
              title="Início"
              to={pages.home}>
              <BriefcaseMedical size={28} strokeWidth={1.5} />
              <span className="mt-1 text-center text-xs font-normal leading-none">
                Início
              </span>
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                cn(
                  'text-background hidden h-full w-24 flex-col items-center justify-center border-t-2 border-transparent transition-all duration-200 sm:flex',
                  isActive
                    ? 'border-secondary text-secondary'
                    : 'hover:text-secondary',
                )
              }
              title="Anotações"
              to="/anotacoes">
              <FileText size={28} strokeWidth={1.5} />
              <span className="mt-1 text-center text-xs font-normal leading-none">
                Anotações
              </span>
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                cn(
                  'text-background hidden h-full w-24 flex-col items-center justify-center border-t-2 border-transparent transition-all duration-200 sm:flex',
                  isActive
                    ? 'border-secondary text-secondary'
                    : 'hover:text-secondary',
                )
              }
              title="Início"
              to={pages.calculators.list}>
              <Calculator size={28} strokeWidth={1.5} />
              <span className="mt-1 text-center text-xs font-light leading-none">
                Calculadoras
              </span>
            </NavLink>

            <button
              className="text-background hover:text-secondary flex w-12 cursor-pointer flex-col items-center justify-center transition-all duration-200 sm:w-20"
              onClick={() => {
                rightSidebar.setOpenMobile(true);
                rightSidebar.setOpen(true);
              }}
              title="Menu">
              <PanelRightOpen size={28} strokeWidth={1.5} />
              <span className="mt-1 text-center text-xs font-light leading-none">
                Menu
              </span>
            </button>
          </div>
        </nav>

        {pathname.includes('/topicos/') && (
          <div className="border-border/30 grid h-[55px] w-full grid-cols-[1fr_auto] items-center border-t px-4 md:grid-cols-[17rem_1fr]">
            <PageTitle
              className="text-background m-0 hidden sm:mb-0 md:flex [&_h1]:text-base [&_h1]:font-normal"
              hasBackButton
              title={backTitle || searchParams.get('name') || 'Voltar'}
            />

            <h1 className="text-background mx-auto w-full max-w-screen-xl px-4 text-xl font-medium sm:text-2xl md:px-8">
              {title}
            </h1>
          </div>
        )}
        {pathname.startsWith('/medicamentos/') && (
          <div className="border-border/30 grid h-16 w-full grid-cols-[1fr_auto] items-center border-t px-4 md:grid-cols-[17rem_1fr]">
            <PageTitle
              className="text-background m-0 hidden sm:mb-0 md:flex [&_h1]:text-base [&_h1]:font-normal"
              hasBackButton
              title="Medicamentos"
            />

            <h1 className="text-background mx-auto w-full max-w-screen-xl px-4 text-xl font-medium sm:text-2xl md:px-8">
              {title}
            </h1>
          </div>
        )}
      </header>
    </div>
  );
}
